import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead, CourseSchema, FAQSchema } from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Palette, Users, Clock, MapPin, CheckCircle, ArrowRight } from 'lucide-react';

const PaintingClassesSanAntonio = () => {
  const faqs = [
    {
      question: "What painting classes are available in San Antonio?",
      answer: "The Coppini Academy of Fine Art offers oil painting classes, alla prima workshops, portrait painting, and landscape painting courses for all skill levels in San Antonio, TX."
    },
    {
      question: "Do I need experience to join painting classes?",
      answer: "No prior experience is required. Our painting classes in San Antonio welcome complete beginners as well as experienced artists looking to refine their technique."
    },
    {
      question: "What materials do I need for painting classes?",
      answer: "Basic oil painting supplies are required. We provide a detailed supply list upon registration, and our instructors can recommend quality art supply stores in San Antonio."
    }
  ];

  return (
    <main className="min-h-screen bg-white">
      <SEOHead
        title="Painting Classes in San Antonio, TX"
        description="Learn oil painting and classical techniques at The Coppini Academy of Fine Art in San Antonio. Expert instruction for beginners to advanced artists. Enroll today!"
        keywords="painting classes San Antonio, oil painting classes San Antonio TX, learn to paint San Antonio, art classes San Antonio, painting lessons Texas"
        canonicalPath="/painting-classes-san-antonio"
      />
      <CourseSchema
        name="Oil Painting Classes in San Antonio"
        description="Classical oil painting instruction for all skill levels at San Antonio's premier fine art academy."
      />
      <FAQSchema faqs={faqs} />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 text-white py-20 lg:py-28">
        <div className="absolute inset-0 opacity-5" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Cpath d='M0 0h40v40H0z' fill='none'/%3E%3Cpath d='M0 20h40M20 0v40' stroke='%23fff' stroke-width='0.5'/%3E%3C/svg%3E\")"}}></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-1 bg-cafa-red/20 text-cafa-gold text-sm font-medium rounded-full mb-6">
              San Antonio&apos;s Premier Art Academy
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-6 leading-tight">
              Painting Classes in San Antonio, TX
            </h1>
            <p className="text-xl text-stone-300 mb-8 leading-relaxed">
              Master the art of oil painting with classical instruction from experienced artists. 
              From foundational techniques to advanced portraiture, discover your artistic potential 
              at The Coppini Academy of Fine Art.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-cafa-red hover:bg-cafa-red-900 text-white">
                <Link to="/classes/adult">View Class Schedule <ArrowRight className="ml-2" size={18} /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 lg:py-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4">
              Why Learn Painting at Coppini Academy?
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Since 1945, we&apos;ve been San Antonio&apos;s destination for serious art education, 
              combining classical training methods with personalized instruction.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Palette, title: "Classical Training", desc: "Learn time-tested techniques from the masters of representational art" },
              { icon: Users, title: "Expert Instructors", desc: "Study with professional artists who exhibit nationally" },
              { icon: Clock, title: "Flexible Scheduling", desc: "Day, evening, and weekend classes to fit your schedule" },
              { icon: MapPin, title: "Central Location", desc: "Conveniently located in the heart of San Antonio" }
            ].map((item, idx) => (
              <Card key={idx} className="border-stone-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <item.icon className="mx-auto mb-4 text-cafa-red" size={40} />
                  <h3 className="font-semibold text-stone-900 mb-2">{item.title}</h3>
                  <p className="text-stone-600 text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Course Offerings */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4 text-center">
            Painting Classes We Offer in San Antonio
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto text-center mb-12">
            Our curriculum covers all aspects of classical oil painting, from foundational 
            skills to advanced techniques used by master painters.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Oil Painting Fundamentals",
                level: "Beginner to Intermediate",
                desc: "Build a solid foundation in oil painting techniques including color mixing, brush handling, and composition. Learn to see like an artist and translate your vision to canvas.",
                topics: ["Color theory and mixing", "Brush techniques", "Canvas preparation", "Value studies", "Still life painting"]
              },
              {
                title: "Portrait Painting",
                level: "Intermediate to Advanced",
                desc: "Master the art of capturing the human likeness in oil. Study facial anatomy, skin tones, and the techniques of classical portrait masters.",
                topics: ["Facial proportions", "Flesh tone mixing", "Lighting the portrait", "Working from life", "Historical techniques"]
              },
              {
                title: "Landscape Painting",
                level: "All Levels",
                desc: "Capture the beauty of the Texas landscape and beyond. Learn atmospheric perspective, plein air techniques, and studio landscape painting.",
                topics: ["Atmospheric perspective", "Plein air painting", "Sky and cloud studies", "Trees and foliage", "Water reflections"]
              },
              {
                title: "Alla Prima Workshops",
                level: "Intermediate to Advanced",
                desc: "Learn the direct painting method favored by many contemporary realists. Complete paintings in single sessions while capturing fresh, spontaneous brushwork.",
                topics: ["Wet-into-wet technique", "Bold brushwork", "Value massing", "Edge control", "Quick studies"]
              }
            ].map((course, idx) => (
              <Card key={idx} className="border-stone-200">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-semibold text-stone-900">{course.title}</h3>
                    <span className="text-xs bg-stone-100 text-stone-600 px-2 py-1 rounded">{course.level}</span>
                  </div>
                  <p className="text-stone-600 mb-4">{course.desc}</p>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-stone-700">What you&apos;ll learn:</p>
                    <ul className="grid grid-cols-2 gap-1">
                      {course.topics.map((topic, i) => (
                        <li key={i} className="text-sm text-stone-600 flex items-center">
                          <CheckCircle size={14} className="text-cafa-red mr-2 flex-shrink-0" />
                          {topic}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Location & Contact */}
      <section className="py-16 lg:py-20 bg-stone-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif mb-6">
                Visit Our San Antonio Studio
              </h2>
              <p className="text-stone-300 mb-6 leading-relaxed">
                Located in the historic Monte Vista neighborhood, The Coppini Academy of Fine Art 
                provides an inspiring environment for artistic growth. Our spacious studios feature 
                excellent natural lighting and professional-grade equipment.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <MapPin className="text-cafa-red mr-3 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="font-medium">The Coppini Academy of Fine Art</p>
                    <p className="text-stone-400">115 Melrose Place</p>
                    <p className="text-stone-400">San Antonio, TX 78212</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Clock className="text-cafa-red mr-3 flex-shrink-0" size={20} />
                  <p className="text-stone-400">Phone: (210) 530-8445</p>
                </div>
              </div>
              <Button asChild className="bg-cafa-red hover:bg-cafa-red-900">
                <Link to="/contact">Get Directions & Contact Info</Link>
              </Button>
            </div>
            <div className="space-y-6">
              <div className="rounded-lg overflow-hidden">
                <iframe
                  src="https://maps.google.com/maps?q=115+Melrose+Place,+San+Antonio,+TX+78212&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Coppini Academy Location - Painting Classes San Antonio"
                  className="grayscale"
                ></iframe>
              </div>
              <div className="bg-stone-800 rounded-lg p-8">
                <h3 className="text-xl font-semibold mb-4">Ready to Start Painting?</h3>
              <p className="text-stone-400 mb-6">
                Join San Antonio&apos;s most respected fine art academy. Whether you&apos;re a complete 
                beginner or an experienced painter, we have a class for you.
              </p>
              <div className="space-y-3">
                <Button asChild className="w-full bg-white text-stone-900 hover:bg-stone-100">
                  <Link to="/classes/adult">Browse All Classes</Link>
                </Button>
                <Button asChild variant="outline" className="w-full border-white text-white hover:bg-white/10">
                  <Link to="/workshops">View Upcoming Workshops</Link>
                </Button>
              </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-serif text-stone-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <div key={idx} className="border-b border-stone-200 pb-6">
                <h3 className="text-lg font-semibold text-stone-900 mb-2">{faq.question}</h3>
                <p className="text-stone-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-cafa-red text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-serif mb-4">
            Begin Your Painting Journey Today
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join the tradition of excellence at San Antonio&apos;s premier fine art academy.
          </p>
          <Button asChild size="lg" className="bg-white text-cafa-red hover:bg-stone-100">
            <Link to="/classes/adult">Enroll in Painting Classes</Link>
          </Button>
        </div>
      </section>
    </main>
  );
};

export default PaintingClassesSanAntonio;
